/**
 * The real `AudioSink` — Web Audio behind the voice limiter in `vfx/audio.ts`.
 *
 * `AudioBus` already owned everything that carries risk: per-sound caps, a total voice
 * ceiling, pitch variance. It was talking to `SilentSink`. This is the other end of that
 * interface and nothing else — no policy lives here, because policy that lives in the
 * browser layer cannot be unit-tested.
 *
 * Every failure degrades to silence, deliberately and at every level: a sample that is
 * not in the manifest, a file that 404s, a buffer that will not decode, a context the
 * browser refuses to start. A sound is never worth breaking a run for, and the game has
 * to be fully playable with the whole `sfx/` directory missing (R-04-35).
 */
import type { AudioSink, PlayOptions, SfxId } from '../vfx/audio.js';

/**
 * Which file plays which sound.
 *
 * Partial ON PURPOSE. `SfxId` is the full vocabulary the game already emits — twenty
 * sounds, wired to events since the VFX layer landed — and the samples arrive one at a
 * time. An id with no entry here is simply silent, which is why the game can ship with
 * one sound in it and grow.
 */
export const SFX_MANIFEST: Partial<Record<SfxId, string>> = {
  // ── Sound is OFF while the samples are still being written ────────────────────────
  //
  // Everything below the manifest is live and tested — the sink, the voice limiter, the
  // silence trimming, the event routing. This map is the only switch, because an id with
  // no entry is silent by design: `load()` decodes nothing, `loadedCount` stays 0, and
  // the app leaves the bus on `SilentSink`.
  //
  // Re-enable by uncommenting. The files are already in `public/assets/audio/sfx/`.
  //
  // shoot_chain: '/assets/audio/sfx/electric.wav',
  // shoot_physical: '/assets/audio/sfx/bullet.wav',
  // explosion: '/assets/audio/sfx/explosion.wav',
};

/** Rate is applied as playbackRate, which also shifts pitch — that is the intent. */
const MIN_RATE = 0.5;
const MAX_RATE = 2;

/** Below this a sample is silence, not a tail. About -56 dBFS. */
const SILENCE_FLOOR = 0.0015;
/** Kept after the last audible sample, so nothing is clipped by a sample or two. */
const TAIL_PAD_SECONDS = 0.02;
/** Not worth reallocating a buffer for less than this. */
const MIN_TRIM_SECONDS = 0.15;

/**
 * Drop trailing silence.
 *
 * A voice slot is held until the BUFFER ends, not until the sound does, so a sample
 * exported with padding occupies the voice limiter long after it stops being audible.
 * The bullet sample arrived as 0.10s of audio in a 1.85s file: eighteen times its own
 * length spent holding a slot, which at four voices per sound meant a firing board
 * cutting off shots that were still audible to make room for shots that were not.
 *
 * Only trims what is provably silent, so a real reverb tail is left alone.
 */
export function trimTrailingSilence(ctx: BaseAudioContext, buffer: AudioBuffer): AudioBuffer {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
    buffer.getChannelData(c),
  );
  let last = -1;
  for (let i = buffer.length - 1; i >= 0; i--) {
    let peak = 0;
    for (const data of channels) {
      const v = Math.abs(data[i] ?? 0);
      if (v > peak) peak = v;
    }
    if (peak > SILENCE_FLOOR) {
      last = i;
      break;
    }
  }
  // Entirely silent, or audible to the very end: nothing to do either way.
  if (last < 0) return buffer;

  const keep = Math.min(buffer.length, last + 1 + Math.ceil(TAIL_PAD_SECONDS * buffer.sampleRate));
  if (buffer.length - keep < MIN_TRIM_SECONDS * buffer.sampleRate) return buffer;

  const trimmed = ctx.createBuffer(buffer.numberOfChannels, keep, buffer.sampleRate);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    trimmed.getChannelData(c).set(channels[c]!.subarray(0, keep));
  }
  return trimmed;
}

export class WebAudioSink implements AudioSink {
  private readonly buffers = new Map<SfxId, AudioBuffer>();
  private readonly sources = new Map<number, AudioBufferSourceNode>();
  private nextHandle = 1;

  /**
   * @param onEnded Handed back to `AudioBus.release` so a sample that finishes on its
   *                own frees its voice slot. Without it the bus would fill with ghosts
   *                and start stopping live sounds to make room for new ones.
   */
  constructor(
    private readonly ctx: AudioContext,
    private readonly destination: AudioNode,
    private readonly onEnded: (handle: number) => void,
  ) {}

  /** Decode everything in the manifest. Resolves even if every single one fails. */
  async load(manifest: Partial<Record<SfxId, string>> = SFX_MANIFEST): Promise<void> {
    await Promise.all(
      (Object.entries(manifest) as [SfxId, string][]).map(async ([id, url]) => {
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          const decoded = await this.ctx.decodeAudioData(await res.arrayBuffer());
          this.buffers.set(id, trimTrailingSilence(this.ctx, decoded));
        } catch {
          // Missing or undecodable: this id stays silent and the rest still load.
        }
      }),
    );
  }

  play(id: SfxId, options: PlayOptions): number | null {
    const buffer = this.buffers.get(id);
    if (!buffer) return null;

    const handle = this.nextHandle++;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = Math.min(MAX_RATE, Math.max(MIN_RATE, options.rate));

    const gain = this.ctx.createGain();
    gain.gain.value = options.volume;
    source.connect(gain);
    gain.connect(this.destination);

    source.onended = () => {
      this.sources.delete(handle);
      gain.disconnect();
      this.onEnded(handle);
    };
    source.start(0);
    this.sources.set(handle, source);
    return handle;
  }

  stop(handle: number): void {
    const source = this.sources.get(handle);
    if (!source) return;
    this.sources.delete(handle);
    // `onended` still fires after an explicit stop, which is what releases the voice.
    try {
      source.stop();
    } catch {
      // Already finished between the bus deciding to evict it and this call.
    }
  }

  get loadedCount(): number {
    return this.buffers.size;
  }

  /** Post-trim length, for checking what a sample actually costs in voice time. */
  durationOf(id: SfxId): number | null {
    return this.buffers.get(id)?.duration ?? null;
  }
}
